/** Auth store — persists access token + user + optional guest session. */
import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { AuthMe, Tokens, User } from "./api";

/**
 * A guest session is granted at the end of the value-first onboarding.
 * The visitor hasn't authenticated yet, but we recognise them well enough
 * to let them preview a workspace shell. Guest access is intentionally
 * limited to demo/preview behaviour — never real customer data.
 */
export interface GuestSession {
  active: boolean;
  /** Business name captured during onboarding, for the guest banner copy. */
  businessName?: string;
  /** ISO timestamp when the guest session was granted. */
  grantedAt?: string;
  /** LocalStorage key that holds the visitor's Brand Brain draft. */
  brandBrainDraftKey?: string;
}

interface AuthStore {
  accessToken: string | null;
  user: User | null;
  memberships: AuthMe["memberships"];
  guest: GuestSession;
  setAuth: (tokens: Tokens) => void;
  setMe: (me: AuthMe) => void;
  clear: () => void;
  grantGuestSession: (session: Omit<GuestSession, "active" | "grantedAt">) => void;
  clearGuestSession: () => void;
  /** End the guest preview entirely: clear guest slice + wipe the
   *  ``mos.onboarding.draft.v1`` localStorage draft that populated it. */
  endGuestPreview: () => void;
}

const EMPTY_GUEST: GuestSession = { active: false };

/** After 24 hours a guest session is considered stale and auto-expires
 *  the next time the app boots. Prevents zombie previews across days. */
const GUEST_SESSION_TTL_MS = 24 * 60 * 60 * 1000;

/** Called on store hydrate — drops the guest session if it's older than
 *  ``GUEST_SESSION_TTL_MS``. */
function expireStaleGuest(state: AuthStore): AuthStore {
  if (!state.guest?.active || !state.guest.grantedAt) return state;
  const age = Date.now() - new Date(state.guest.grantedAt).getTime();
  if (age > GUEST_SESSION_TTL_MS) {
    return { ...state, guest: EMPTY_GUEST };
  }
  return state;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      accessToken: null,
      user: null,
      memberships: [],
      guest: EMPTY_GUEST,
      setAuth: (t) =>
        set({
          accessToken: t.access_token,
          user: t.user,
          // Once authenticated, the guest session is no longer needed.
          guest: EMPTY_GUEST,
        }),
      setMe: (m) => set({ user: m.user, memberships: m.memberships }),
      clear: () => set({ accessToken: null, user: null, memberships: [], guest: EMPTY_GUEST }),
      grantGuestSession: (session) =>
        set({
          guest: {
            active: true,
            grantedAt: new Date().toISOString(),
            ...session,
          },
        }),
      clearGuestSession: () => set({ guest: EMPTY_GUEST }),
      endGuestPreview: () => {
        try {
          window.localStorage.removeItem("mos.onboarding.draft.v1");
        } catch { /* localStorage may be unavailable */ }
        set({ guest: EMPTY_GUEST });
      },
    }),
    {
      name: "cz.auth-store",
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        // Mutate in place so persist doesn't have to re-write immediately.
        const fresh = expireStaleGuest(state as AuthStore);
        if (fresh.guest !== state.guest) state.guest = fresh.guest;
      },
    },
  ),
);
